import { isSaasPlatformHost, isToolsAndBubblesHost } from '../../share/serviceappBrand';

const ORIGIN_KEY = 'saas_oauth_return_origin';
const PATH_KEY = 'saas_oauth_return_path';

export const DEFAULT_SAAS_PLATFORM_ORIGIN = 'https://serviceapp.ai';

/** Canonical ServiceApp platform origin (env override for staging). */
export function resolveSaasPlatformOrigin(): string {
  if (typeof window !== 'undefined' && isSaasPlatformHost(window.location.hostname)) {
    return window.location.origin;
  }
  const env = (import.meta.env && (import.meta.env.VITE_SAAS_APP_URL as string | undefined))?.trim();
  if (env) return env.replace(/\/$/, '');
  return DEFAULT_SAAS_PLATFORM_ORIGIN;
}

export function isOAuthCallbackUrl(): boolean {
  if (typeof window === 'undefined') return false;
  const hash = window.location.hash.toLowerCase();
  if (hash.includes('access_token') || hash.includes('type=recovery') || hash.includes('error=')) {
    return true;
  }
  return new URLSearchParams(window.location.search).has('code');
}

/** Remember where Google/SSO was started so we can recover if Supabase falls back to Site URL. */
export function stashSaasOAuthReturn(): void {
  if (typeof window === 'undefined') return;
  if (!isSaasPlatformHost(window.location.hostname)) return;
  try {
    sessionStorage.setItem(ORIGIN_KEY, window.location.origin);
    sessionStorage.setItem(PATH_KEY, `${window.location.pathname}${window.location.search}`);
  } catch {
    /* ignore quota / private mode */
  }
}

function readStashedOAuthReturn(): { origin: string; path: string } | null {
  if (typeof window === 'undefined') return null;
  try {
    const origin = sessionStorage.getItem(ORIGIN_KEY)?.trim();
    if (!origin) return null;
    const parsed = new URL(origin);
    if (!isSaasPlatformHost(parsed.hostname)) return null;
    const path = sessionStorage.getItem(PATH_KEY) || '/login';
    return { origin: parsed.origin, path };
  } catch {
    return null;
  }
}

export function clearStashedOAuthReturn(): void {
  try {
    sessionStorage.removeItem(ORIGIN_KEY);
    sessionStorage.removeItem(PATH_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Supabase Auth Site URL may still be toolsandbubbles.com. When OAuth completes on
 * the wrong host, bounce back to the stashed ServiceApp origin with tokens intact.
 */
export function recoverSaasOAuthReturnIfNeeded(): boolean {
  if (typeof window === 'undefined') return false;
  if (isSaasPlatformHost(window.location.hostname)) return false;
  if (!isOAuthCallbackUrl()) return false;
  // Native T&B Google/SSO must never be redirected to ServiceApp (stale stash from testing).
  if (isToolsAndBubblesHost(window.location.hostname)) {
    clearStashedOAuthReturn();
    return false;
  }

  const stashed = readStashedOAuthReturn();
  if (!stashed) return false;

  clearStashedOAuthReturn();
  const dest = `${stashed.origin}${stashed.path}${window.location.search}${window.location.hash}`;
  window.location.replace(dest);
  return true;
}

/** Build a ServiceApp URL that carries the current Supabase session (cross-origin handoff). */
export async function buildSaasPlatformHandoffUrl(path: string): Promise<string> {
  const origin = resolveSaasPlatformOrigin().replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (typeof window === 'undefined') {
    return `${origin}${normalizedPath}`;
  }

  try {
    const { supabase } = await import('../../lib/supabase.js');
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.access_token && session.refresh_token) {
      const hash = new URLSearchParams({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        token_type: 'bearer',
      }).toString();
      return `${origin}${normalizedPath}#${hash}`;
    }
  } catch {
    /* ignore — fall back to path-only redirect */
  }

  return `${origin}${normalizedPath}`;
}

/**
 * Optional explicit handoff to ServiceApp /admin (with session tokens in hash).
 * Not used automatically after toolsandbubbles.com login — T&B sessions are per-origin.
 */
export async function redirectPlatformAdminToSaasConsole(isPlatformAdmin: boolean): Promise<boolean> {
  if (!isPlatformAdmin || typeof window === 'undefined') return false;
  if (isSaasPlatformHost(window.location.hostname)) return false;

  clearStashedOAuthReturn();
  const dest = await buildSaasPlatformHandoffUrl('/admin');
  window.location.replace(dest);
  return true;
}
